import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, Link, Outlet, useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  LayoutDashboard, MapPin, Calendar, MessageSquare, Star, 
  LogOut, Menu, X, ChevronRight, Database, Info, Newspaper, Images, Briefcase, Settings, FolderOpen, ChevronDown, StickyNote, Pencil, Users, KeyRound, Ticket, Sparkles, DollarSign, Quote, Bell, BellOff, FileBarChart, TrendingUp, Globe, Plane
} from 'lucide-react';
import { toast } from 'sonner';
import ChangePasswordModal from './ChangePasswordModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Dashboard Overview Component - defined outside to avoid re-renders
const DashboardOverview = ({ stats, adminUsername, onSeedData, notificationCounts }) => (
  <div className="space-y-8">
    {/* Welcome Header */}
    <div className="relative overflow-hidden bg-gradient-to-br from-gray-900 via-gray-800 to-amber-900 rounded-2xl p-8 text-white">
      <div className="absolute top-0 right-0 w-64 h-64 bg-amber-500/10 rounded-full -translate-y-1/2 translate-x-1/2" />
      <div className="absolute bottom-0 left-0 w-48 h-48 bg-amber-500/5 rounded-full translate-y-1/2 -translate-x-1/2" />
      <div className="relative">
        <div className="flex justify-between items-start">
          <div>
            <p className="text-amber-400 text-sm font-medium mb-1">Welcome back</p>
            <h1 className="text-3xl font-bold">{adminUsername}</h1>
            <p className="text-gray-400 mt-2 max-w-md">Manage your travel agency, track inquiries, and create amazing travel experiences.</p>
          </div>
          <Button onClick={onSeedData} variant="outline" className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10 hover:text-amber-300">
            <Database className="w-4 h-4 mr-2" />
            Seed Data
          </Button>
        </div>
      </div>
    </div>

    {/* Stats Grid */}
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      <Card className="group hover:shadow-lg hover:shadow-blue-500/10 transition-all duration-300 border-l-4 border-l-blue-500">
        <CardContent className="p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">Destinations</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">{stats.destinations}</p>
            </div>
            <div className="p-3 bg-blue-50 rounded-xl group-hover:bg-blue-100 transition-colors">
              <Globe className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="group hover:shadow-lg hover:shadow-amber-500/10 transition-all duration-300 border-l-4 border-l-amber-500">
        <CardContent className="p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">Itineraries</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">{stats.itineraries}</p>
            </div>
            <div className="p-3 bg-amber-50 rounded-xl group-hover:bg-amber-100 transition-colors">
              <Plane className="w-6 h-6 text-amber-600" />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="group hover:shadow-lg hover:shadow-emerald-500/10 transition-all duration-300 border-l-4 border-l-emerald-500">
        <CardContent className="p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">Inquiries</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">{stats.inquiries}</p>
            </div>
            <div className="p-3 bg-emerald-50 rounded-xl group-hover:bg-emerald-100 transition-colors">
              <MessageSquare className="w-6 h-6 text-emerald-600" />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="group hover:shadow-lg hover:shadow-purple-500/10 transition-all duration-300 border-l-4 border-l-purple-500">
        <CardContent className="p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">Testimonials</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">{stats.testimonials}</p>
            </div>
            <div className="p-3 bg-purple-50 rounded-xl group-hover:bg-purple-100 transition-colors">
              <Star className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>

    {/* Activity Overview */}
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Pending Items */}
      <Card className="overflow-hidden">
        <CardHeader className="bg-gradient-to-r from-amber-50 to-orange-50 border-b">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Bell className="w-5 h-5 text-amber-600" />
            Pending Actions
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="divide-y">
            <Link to="/admin/inquiries" className="flex items-center justify-between px-5 py-4 hover:bg-gray-50 transition-colors">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                  <MessageSquare className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">New Inquiries</p>
                  <p className="text-sm text-gray-500">Customer travel requests</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {notificationCounts?.inquiries > 0 && (
                  <span className="px-3 py-1 bg-green-100 text-green-700 text-sm font-semibold rounded-full">{notificationCounts.inquiries}</span>
                )}
                <ChevronRight className="w-5 h-5 text-gray-400" />
              </div>
            </Link>
            <Link to="/admin/visa-inquiries" className="flex items-center justify-between px-5 py-4 hover:bg-gray-50 transition-colors">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                  <StickyNote className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">Visa Applications</p>
                  <p className="text-sm text-gray-500">Pending visa requests</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {notificationCounts?.visaInquiries > 0 && (
                  <span className="px-3 py-1 bg-blue-100 text-blue-700 text-sm font-semibold rounded-full">{notificationCounts.visaInquiries}</span>
                )}
                <ChevronRight className="w-5 h-5 text-gray-400" />
              </div>
            </Link>
            <Link to="/admin/live-chat" className="flex items-center justify-between px-5 py-4 hover:bg-gray-50 transition-colors">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                  <MessageSquare className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">Live Chat</p>
                  <p className="text-sm text-gray-500">Customers waiting for support</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {notificationCounts?.liveChats > 0 && (
                  <span className="px-3 py-1 bg-red-100 text-red-700 text-sm font-semibold rounded-full animate-pulse">{notificationCounts.liveChats}</span>
                )}
                <ChevronRight className="w-5 h-5 text-gray-400" />
              </div>
            </Link>
            <Link to="/admin/instant-itineraries" className="flex items-center justify-between px-5 py-4 hover:bg-gray-50 transition-colors">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">Instant Itineraries</p>
                  <p className="text-sm text-gray-500">AI-generated trip plans</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {notificationCounts?.instantItineraries > 0 && (
                  <span className="px-3 py-1 bg-purple-100 text-purple-700 text-sm font-semibold rounded-full">{notificationCounts.instantItineraries}</span>
                )}
                <ChevronRight className="w-5 h-5 text-gray-400" />
              </div>
            </Link>
          </div>
        </CardContent>
      </Card>

      {/* Quick Stats & Reports */}
      <Card className="overflow-hidden">
        <CardHeader className="bg-gradient-to-r from-emerald-50 to-teal-50 border-b">
          <CardTitle className="flex items-center gap-2 text-lg">
            <TrendingUp className="w-5 h-5 text-emerald-600" />
            Quick Access
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="divide-y">
            <Link to="/admin/reports" className="flex items-center justify-between px-5 py-4 hover:bg-gray-50 transition-colors">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                  <FileBarChart className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">Reports & Exports</p>
                  <p className="text-sm text-gray-500">Download transaction data</p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400" />
            </Link>
            <Link to="/admin/chat-leads" className="flex items-center justify-between px-5 py-4 hover:bg-gray-50 transition-colors">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-pink-100 flex items-center justify-center">
                  <Users className="w-5 h-5 text-pink-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">Chat Leads</p>
                  <p className="text-sm text-gray-500">AI chatbot captures</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {notificationCounts?.chatLeads > 0 && (
                  <span className="px-3 py-1 bg-pink-100 text-pink-700 text-sm font-semibold rounded-full">{notificationCounts.chatLeads}</span>
                )}
                <ChevronRight className="w-5 h-5 text-gray-400" />
              </div>
            </Link>
            <Link to="/admin/vouchers" className="flex items-center justify-between px-5 py-4 hover:bg-gray-50 transition-colors">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                  <Ticket className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">Vouchers</p>
                  <p className="text-sm text-gray-500">Generate travel vouchers</p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400" />
            </Link>
            <Link to="/admin/chat-analytics" className="flex items-center justify-between px-5 py-4 hover:bg-gray-50 transition-colors">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-indigo-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">Chat Analytics</p>
                  <p className="text-sm text-gray-500">AI assistant insights</p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400" />
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  </div>
);

const AdminDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [showChangePw, setShowChangePw] = useState(false);
  const [openGroups, setOpenGroups] = useState({ 'Website Editing': true, 'Inquiries': true, 'About Us': true });
  const toggleGroup = (label) => setOpenGroups((p) => ({ ...p, [label]: !p[label] }));
  const [stats, setStats] = useState({
    destinations: 0,
    itineraries: 0,
    inquiries: 0,
    testimonials: 0
  });
  const adminUsername = localStorage.getItem('adminUsername');
  const adminAuth = localStorage.getItem('adminAuth');
  
  // Notification system
  const [notificationsEnabled, setNotificationsEnabled] = useState(() => {
    const saved = localStorage.getItem('adminNotificationsEnabled');
    return saved !== 'false'; // Default to true
  });
  const [notificationCounts, setNotificationCounts] = useState({
    inquiries: 0,
    visaInquiries: 0,
    liveChats: 0,
    liveMessages: 0, // Track total messages for live chat
    instantItineraries: 0,
    chatLeads: 0,
    savedItineraries: 0
  });
  const prevCountsRef = useRef({ 
    inquiries: 0, visaInquiries: 0, liveChats: 0, liveMessages: 0, 
    instantItineraries: 0, chatLeads: 0, savedItineraries: 0 
  });
  const audioRefs = useRef({
    liveChat: null,
    inquiry: null,
    visa: null,
    lead: null,
    itinerary: null
  });
  const hasInitializedRef = useRef(false);
  
  // Different notification sounds for each type (using different frequencies)
  const playNotificationSound = useCallback((type) => {
    if (!notificationsEnabled) return;
    
    // Create audio context for different tones
    try {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      const audioCtx = new AudioContext();
      const oscillator = audioCtx.createOscillator();
      const gainNode = audioCtx.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioCtx.destination);
      
      // Different frequencies for different notification types
      const sounds = {
        liveChat: { freq: 880, duration: 0.15, pattern: [1, 0.1, 1] }, // High beep-beep (A5)
        inquiry: { freq: 523, duration: 0.2, pattern: [1] }, // Medium beep (C5)
        visa: { freq: 659, duration: 0.2, pattern: [1] }, // Higher beep (E5)
        lead: { freq: 784, duration: 0.12, pattern: [1, 0.08, 1, 0.08, 1] }, // Triple beep (G5)
        itinerary: { freq: 440, duration: 0.3, pattern: [1] }, // Low beep (A4)
        saved: { freq: 587, duration: 0.2, pattern: [1] } // D5
      };
      
      const sound = sounds[type] || sounds.inquiry;
      oscillator.frequency.setValueAtTime(sound.freq, audioCtx.currentTime);
      oscillator.type = 'sine';
      
      gainNode.gain.setValueAtTime(0.3, audioCtx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioCtx.currentTime + sound.duration);
      
      oscillator.start(audioCtx.currentTime);
      oscillator.stop(audioCtx.currentTime + sound.duration);
      
      // For patterns with multiple beeps (like lead notification)
      if (sound.pattern.length > 1) {
        let time = sound.duration + 0.1;
        for (let i = 1; i < sound.pattern.length; i += 2) {
          setTimeout(() => {
            const osc2 = audioCtx.createOscillator();
            const gain2 = audioCtx.createGain();
            osc2.connect(gain2);
            gain2.connect(audioCtx.destination);
            osc2.frequency.setValueAtTime(sound.freq, audioCtx.currentTime);
            osc2.type = 'sine';
            gain2.gain.setValueAtTime(0.3, audioCtx.currentTime);
            gain2.gain.exponentialRampToValueAtTime(0.01, audioCtx.currentTime + sound.duration);
            osc2.start(audioCtx.currentTime);
            osc2.stop(audioCtx.currentTime + sound.duration);
          }, time * 1000);
          time += sound.duration + (sound.pattern[i] || 0.1);
        }
      }
    } catch (e) {
      console.log('Audio play failed:', e);
    }
  }, [notificationsEnabled]);
  
  // Toggle notifications
  const toggleNotifications = () => {
    const newValue = !notificationsEnabled;
    setNotificationsEnabled(newValue);
    localStorage.setItem('adminNotificationsEnabled', newValue.toString());
    toast.success(newValue ? 'Notifications enabled' : 'Notifications muted');
  };
  
  // Fetch notification counts
  const fetchNotificationCounts = useCallback(async () => {
    if (!adminAuth) return;
    
    try {
      const headers = { 'Authorization': `Basic ${adminAuth}` };
      
      const [inqRes, liveRes, instantRes, leadsRes, savedRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/inquiries`, { headers }).catch(() => ({ ok: false })),
        fetch(`${API_URL}/api/admin/live-chats/unread-count`, { headers }).catch(() => ({ ok: false })),
        fetch(`${API_URL}/api/admin/instant-itineraries`, { headers }).catch(() => ({ ok: false })),
        fetch(`${API_URL}/api/admin/chat-leads`, { headers }).catch(() => ({ ok: false })),
        fetch(`${API_URL}/api/admin/saved-itineraries`, { headers }).catch(() => ({ ok: false }))
      ]);
      
      const newCounts = {
        inquiries: 0,
        visaInquiries: 0,
        liveChats: 0,
        liveMessages: 0,
        instantItineraries: 0,
        chatLeads: 0,
        savedItineraries: 0
      };
      
      if (inqRes.ok) {
        const data = await inqRes.json();
        // Split inquiries: normal vs visa
        const normalInquiries = Array.isArray(data) ? data.filter(i => i.inquiry_type !== 'visa') : [];
        const visaInquiries = Array.isArray(data) ? data.filter(i => i.inquiry_type === 'visa') : [];
        newCounts.inquiries = normalInquiries.filter(i => i.status === 'new' || !i.status).length;
        newCounts.visaInquiries = visaInquiries.filter(i => i.status === 'new' || !i.status).length;
      }
      
      if (liveRes.ok) {
        const data = await liveRes.json();
        newCounts.liveChats = data.waiting || 0;
        newCounts.liveMessages = data.unread || 0; // Total unread messages
      }
      
      if (instantRes.ok) {
        const data = await instantRes.json();
        newCounts.instantItineraries = Array.isArray(data) ? data.filter(i => i.status === 'pending' || !i.status).length : 0;
      }
      
      if (leadsRes.ok) {
        const data = await leadsRes.json();
        newCounts.chatLeads = Array.isArray(data) ? data.filter(l => l.status === 'new' || !l.status).length : 0;
      }
      
      if (savedRes.ok) {
        const data = await savedRes.json();
        newCounts.savedItineraries = Array.isArray(data) ? data.filter(i => i.status === 'new' || !i.status).length : 0;
      }
      
      // Check for new items and play appropriate sound
      if (hasInitializedRef.current) {
        const prev = prevCountsRef.current;
        
        // Live chat - check both waiting sessions AND message counts
        if (newCounts.liveChats > prev.liveChats || newCounts.liveMessages > prev.liveMessages) {
          playNotificationSound('liveChat');
          toast.info('💬 New live chat message!', { duration: 5000 });
        } else if (newCounts.chatLeads > prev.chatLeads) {
          playNotificationSound('lead');
          toast.info('🎯 New lead captured!', { duration: 5000 });
        } else if (newCounts.inquiries > prev.inquiries) {
          playNotificationSound('inquiry');
          toast.info('📩 New inquiry received!', { duration: 5000 });
        } else if (newCounts.visaInquiries > prev.visaInquiries) {
          playNotificationSound('visa');
          toast.info('🛂 New visa inquiry received!', { duration: 5000 });
        } else if (newCounts.instantItineraries > prev.instantItineraries) {
          playNotificationSound('itinerary');
          toast.info('✨ New instant itinerary request!', { duration: 5000 });
        } else if (newCounts.savedItineraries > prev.savedItineraries) {
          playNotificationSound('saved');
          toast.info('📋 New saved itinerary!', { duration: 5000 });
        }
      } else {
        hasInitializedRef.current = true;
      }
      
      prevCountsRef.current = newCounts;
      setNotificationCounts(newCounts);
      
    } catch (err) {
      console.log('Failed to fetch notification counts:', err);
    }
  }, [adminAuth, playNotificationSound]);
  
  // Poll for notifications every 5 seconds (faster for live chat)
  useEffect(() => {
    fetchNotificationCounts();
    const interval = setInterval(fetchNotificationCounts, 5000);
    return () => clearInterval(interval);
  }, [fetchNotificationCounts]);

  const fetchStats = useCallback(async () => {
    try {
      const headers = {
        'Authorization': `Basic ${adminAuth}`,
        'Content-Type': 'application/json'
      };

      const [destRes, itinRes, inqRes, testRes] = await Promise.all([
        fetch(`${API_URL}/api/destinations`),
        fetch(`${API_URL}/api/itineraries`),
        fetch(`${API_URL}/api/admin/inquiries`, { headers }),
        fetch(`${API_URL}/api/testimonials`)
      ]);

      const destinations = await destRes.json();
      const itineraries = await itinRes.json();
      const testimonials = await testRes.json();
      
      let inquiries = [];
      if (inqRes.ok) {
        inquiries = await inqRes.json();
      }

      setStats({
        destinations: destinations.length || 0,
        itineraries: itineraries.length || 0,
        inquiries: inquiries.length || 0,
        testimonials: testimonials.length || 0
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, [adminAuth]);

  useEffect(() => {
    if (!adminAuth) {
      navigate('/admin/login');
      return;
    }
    fetchStats();
  }, [adminAuth, navigate, fetchStats]);

  const handleLogout = () => {
    localStorage.removeItem('adminAuth');
    localStorage.removeItem('adminUsername');
    toast.success('Logged out successfully');
    navigate('/admin/login');
  };

  const handleSeedData = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/seed-data`, {
        method: 'POST',
        headers: {
          'Authorization': `Basic ${adminAuth}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      toast.success(data.message);
      fetchStats();
    } catch (error) {
      toast.error('Failed to seed data');
    }
  };

  const role = localStorage.getItem('adminRole') || 'admin';
  const canEditing = ['super', 'admin', 'manager', 'editor'].includes(role);
  const canInquiries = ['super', 'admin', 'manager', 'csr'].includes(role);
  const isSuper = role === 'super';

  // Get badge count for menu item
  const getBadgeCount = (path) => {
    switch (path) {
      case '/admin/inquiries': return notificationCounts.inquiries;
      case '/admin/visa-inquiries': return notificationCounts.visaInquiries;
      case '/admin/live-chat': return notificationCounts.liveChats;
      case '/admin/instant-itineraries': return notificationCounts.instantItineraries;
      case '/admin/saved-itineraries': return notificationCounts.savedItineraries;
      case '/admin/chat-leads': return notificationCounts.chatLeads;
      default: return 0;
    }
  };

  // Get total badge count for a group
  const getGroupBadgeCount = (children) => {
    return children.reduce((sum, child) => {
      if (child.children) return sum + getGroupBadgeCount(child.children);
      return sum + getBadgeCount(child.path);
    }, 0);
  };

  const websiteEditingGroup = {
    label: 'Website Editing', icon: Pencil, children: [
      { path: '/admin/homepage-images', icon: Database, label: 'Homepage Images' },
      { path: '/admin/destinations', icon: MapPin, label: 'Destinations' },
      { path: '/admin/visas', icon: StickyNote, label: 'Visa Services' },
      { path: '/admin/itineraries', icon: Calendar, label: 'Itineraries' },
      { path: '/admin/services', icon: Briefcase, label: 'Services' },
      { path: '/admin/blog', icon: Newspaper, label: 'Blog' },
      {
        label: 'About Us', icon: Info, children: [
          { path: '/admin/about', icon: Info, label: 'About Page' },
          { path: '/admin/gallery', icon: Images, label: 'Gallery' },
          { path: '/admin/testimonials', icon: Star, label: 'Testimonials' },
        ],
      },
      {
        label: 'Instant Itinerary', icon: Sparkles, children: [
          { path: '/admin/travel-quotes', icon: Quote, label: 'Travel Quotes' },
        ],
      },
      { path: '/admin/settings', icon: Settings, label: 'Settings' },
    ],
  };

  const inquiriesGroup = {
    label: 'Inquiries', icon: MessageSquare, children: [
      { path: '/admin/inquiries', icon: MessageSquare, label: 'Inquiries' },
      { path: '/admin/visa-inquiries', icon: StickyNote, label: 'Visa Inquiries' },
      { path: '/admin/saved-itineraries', icon: FolderOpen, label: 'Saved Itineraries' },
      { path: '/admin/instant-itineraries', icon: Sparkles, label: 'Instant Itineraries' },
      { path: '/admin/vouchers', icon: Ticket, label: 'Vouchers' },
      { path: '/admin/live-chat', icon: MessageSquare, label: 'Live Chat', highlight: true },
      { path: '/admin/chat-leads', icon: Users, label: 'Chat Leads' },
      { path: '/admin/chat-analytics', icon: MessageSquare, label: 'Chat Analytics' },
    ],
  };

  const menuItems = [
    { path: '/admin/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    ...(canEditing ? [websiteEditingGroup] : []),
    ...(canInquiries ? [inquiriesGroup] : []),
    { path: '/admin/reports', icon: FileBarChart, label: 'Reports' },
    ...(isSuper ? [{ path: '/admin/pricing', icon: DollarSign, label: 'Geo Pricing' }] : []),
    ...(isSuper ? [{ path: '/admin/discount-codes', icon: Ticket, label: 'Discount Codes' }] : []),
    ...(isSuper ? [{ path: '/admin/users', icon: Users, label: 'Admin Users' }] : []),
  ];

  const isActive = (path) => location.pathname === path;

  const renderNode = (item, depth) => {
    const Icon = item.icon;
    if (item.children) {
      const open = openGroups[item.label];
      const childActive = item.children.some((c) => (c.children ? c.children.some((g) => isActive(g.path)) : isActive(c.path)));
      const groupBadge = getGroupBadgeCount(item.children);
      return (
        <div key={item.label}>
          <button
            onClick={() => toggleGroup(item.label)}
            data-testid={`admin-group-${item.label.replace(/\s+/g, '-').toLowerCase()}`}
            className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors w-full ${
              childActive ? 'bg-gray-800 text-white' : (depth === 0 ? 'text-gray-200' : 'text-gray-400') + ' hover:bg-gray-800 hover:text-white'
            } ${depth === 0 ? 'font-semibold' : ''}`}
          >
            <Icon className="w-5 h-5 shrink-0" />
            {sidebarOpen && <span className="flex-1 text-left text-sm">{item.label}</span>}
            {sidebarOpen && groupBadge > 0 && (
              <span className="px-2 py-0.5 text-xs bg-red-500 text-white rounded-full min-w-[20px] text-center">
                {groupBadge > 99 ? '99+' : groupBadge}
              </span>
            )}
            {sidebarOpen && <ChevronDown className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} />}
          </button>
          {open && (
            <div className={sidebarOpen ? 'ml-3 mt-1 space-y-1 border-l border-gray-800 pl-2' : 'mt-1 space-y-1'}>
              {item.children.map((child) => renderNode(child, depth + 1))}
            </div>
          )}
        </div>
      );
    }
    
    const badgeCount = getBadgeCount(item.path);
    return (
      <Link
        key={item.path}
        to={item.path}
        className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-sm ${
          isActive(item.path) ? 'bg-amber-600 text-white' : 'text-gray-400 hover:bg-gray-800 hover:text-white'
        }`}
      >
        <Icon className="w-4 h-4 shrink-0" />
        {sidebarOpen && <span className="flex-1">{item.label}</span>}
        {sidebarOpen && badgeCount > 0 && (
          <span className={`px-2 py-0.5 text-xs rounded-full min-w-[20px] text-center ${
            item.highlight ? 'bg-red-500 text-white animate-pulse' : 'bg-amber-500 text-white'
          }`}>
            {badgeCount > 99 ? '99+' : badgeCount}
          </span>
        )}
      </Link>
    );
  };

  return (
    <div className="min-h-screen bg-gray-100 flex">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 bg-gray-900 transition-all duration-300 ${sidebarOpen ? 'w-64' : 'w-20'}`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-4 border-b border-gray-800">
            <div className="flex items-center justify-between">
              {sidebarOpen && (
                <div>
                  <h2 className="text-white font-bold text-lg">TVL Admin</h2>
                  <p className="text-gray-400 text-xs">Management Panel</p>
                </div>
              )}
              <div className="flex items-center gap-2">
                {/* Notification Bell */}
                <button 
                  onClick={toggleNotifications}
                  className={`relative p-2 rounded-lg transition-colors ${notificationsEnabled ? 'text-amber-400 hover:bg-gray-800' : 'text-gray-600 hover:bg-gray-800'}`}
                  title={notificationsEnabled ? 'Notifications On - Click to mute' : 'Notifications Muted - Click to enable'}
                >
                  {notificationsEnabled ? <Bell className="w-5 h-5" /> : <BellOff className="w-5 h-5" />}
                  {/* Notification badge */}
                  {(notificationCounts.inquiries + notificationCounts.visaInquiries + notificationCounts.liveChats + notificationCounts.instantItineraries) > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center animate-pulse">
                      {Math.min(notificationCounts.inquiries + notificationCounts.visaInquiries + notificationCounts.liveChats + notificationCounts.instantItineraries, 99)}
                    </span>
                  )}
                </button>
                <button 
                  onClick={() => setSidebarOpen(!sidebarOpen)}
                  className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg"
                >
                  {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                </button>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1.5 overflow-y-auto">
            {menuItems.map((item) => renderNode(item, 0))}
          </nav>

          {/* Account actions */}
          <div className="p-4 border-t border-gray-800 space-y-1">
            <button
              onClick={() => setShowChangePw(true)}
              data-testid="open-change-password"
              className="flex items-center gap-3 px-4 py-3 w-full text-gray-400 hover:bg-gray-800 hover:text-white rounded-lg transition-colors"
            >
              <KeyRound className="w-5 h-5 shrink-0" />
              {sidebarOpen && <span>Change Password</span>}
            </button>
            <button
              onClick={handleLogout}
              className="flex items-center gap-3 px-4 py-3 w-full text-gray-400 hover:bg-gray-800 hover:text-white rounded-lg transition-colors"
            >
              <LogOut className="w-5 h-5 shrink-0" />
              {sidebarOpen && <span>Logout</span>}
            </button>
          </div>
        </div>
      </aside>

      {showChangePw && <ChangePasswordModal onClose={() => setShowChangePw(false)} />}

      {/* Main Content */}
      <main className={`flex-1 transition-all duration-300 ${sidebarOpen ? 'ml-64' : 'ml-20'}`}>
        <div className="p-8">
          {location.pathname === '/admin/dashboard' ? (
            <DashboardOverview 
              stats={stats} 
              adminUsername={adminUsername} 
              onSeedData={handleSeedData}
              notificationCounts={notificationCounts}
            />
          ) : (
            <Outlet context={{ adminAuth, fetchStats }} />
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
